import { useEffect, useRef } from "react";
import Popup from "@common/components/Popup";
import type { MgbaCore } from "../../emulator/types";
import { useGuide } from "../useGuide";
import { GuideBrowser } from "./GuideBrowser";
import { MarkdownRenderer } from "./MarkdownRenderer";

// The player puts the sidebar beside the game above 880px (index.css).
const SIDE_BY_SIDE = "(min-width: 881px)";
const MAX_SHARE = 0.5;

// The panel's own close button and the player's guide button both hide
// it (`onHide`); the player decides where it sits (index.css).
export function GuidePanel({
  core,
  currentRom,
  onHide,
}: {
  core: MgbaCore | null;
  currentRom: string | null;
  onHide: () => void;
}) {
  const guide = useGuide(core, currentRom);
  const contentRef = useRef<HTMLDivElement>(null);

  const currentSection = guide.sections.find((s) => s.id === guide.progress.currentSectionId);

  // Restore scroll position when section changes
  useEffect(() => {
    if (contentRef.current && currentSection && guide.progress.scrollOffset > 0) {
      contentRef.current.scrollTop = guide.progress.scrollOffset;
    }
  }, [currentSection, guide.progress.scrollOffset]);

  // Remember the width the user dragged the guide to. The sidebar column
  // takes its width from the guide (panel padding and border add 34px). Only
  // while it sits beside the game, and at most half the window: under the
  // game it spans the page, which once got saved and squeezed the game out.
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const layout = el.closest<HTMLElement>(".layout");
    const beside = window.matchMedia(SIDE_BY_SIDE);
    const applyWidth = (width: number) => layout?.style.setProperty("--aside-width", `${Math.round(width) + 34}px`);
    try {
      const width = Number(localStorage.getItem("guide-width"));
      if (width >= 240 && width <= window.innerWidth * MAX_SHARE && beside.matches) {
        el.style.width = `${width}px`;
        applyWidth(width);
      }
    } catch {
      // Ignore storage errors
    }
    let timer: number | undefined;
    let first = true;
    const observer = new ResizeObserver(() => {
      if (first) {
        first = false;
        return;
      }
      if (!beside.matches) return;
      const rect = el.getBoundingClientRect();
      applyWidth(rect.width);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        try {
          localStorage.setItem("guide-width", String(Math.round(rect.width)));
        } catch {
          // Ignore storage errors
        }
      }, 300);
    });
    observer.observe(el);
    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
      layout?.style.removeProperty("--aside-width");
    };
  }, [currentSection?.id]);

  // Save scroll position
  const handleScroll = () => {
    if (contentRef.current) {
      guide.setScrollOffset(contentRef.current.scrollTop);
    }
  };

  // No guide for this game (or not known yet): no panel at all.
  if (!guide.available || guide.isLoading) return null;

  if (guide.error) {
    return (
      <Popup title="Guide" className="guide" onClose={onHide}>
        <p className="kui-muted kui-small error">Failed to load guide: {guide.error}</p>
      </Popup>
    );
  }

  const content = (
    <>
      <GuideBrowser
        sections={guide.sections}
        currentSectionId={guide.progress.currentSectionId}
        onSelectSection={guide.setCurrentSection}
      />

      {currentSection ? (
        <div className="guide-section">
          {currentSection.location && currentSection.location !== currentSection.title && (
            <p className="guide-location kui-muted kui-small">{currentSection.location}</p>
          )}
          <div ref={contentRef} className="guide-body" onScroll={handleScroll}>
            <MarkdownRenderer
              markdown={currentSection.markdown}
              onCheckboxChange={guide.updateCheckbox}
              savedCheckboxState={guide.progress.checkboxState}
            />
          </div>
        </div>
      ) : (
        <p className="kui-muted kui-small">Select a section to start.</p>
      )}
    </>
  );

  return (
    <Popup title="Guide" className="guide" onClose={onHide}>
      {content}
    </Popup>
  );
}
